function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function contributionMarginFromScenarioKpis(kpis = {}) {
  const raw =
    kpis.expectedContributionMargin ??
    kpis.expected_contribution_margin ??
    kpis.contributionMargin ??
    kpis.contribution_margin ??
    kpis.expectedProfit;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Scenario weeks from Simulator save payload or legacy shapes */
export function scenarioWeekRange(scenario) {
  const w = scenario?.weeks || {};
  const start = num(w.startWeek ?? w.start ?? w.week);
  const endRaw = w.endWeek ?? w.end ?? w.week ?? start;
  const end = num(endRaw);
  if (start == null) return null;
  const hi = end != null ? end : start;
  return { start, end: Math.max(start, hi) };
}

export function scenarioOverlapsWeekSelection(scenario, weekStart, weekEnd) {
  if (weekStart == null || weekEnd == null) return true;
  const r = scenarioWeekRange(scenario);
  if (!r) return true;
  return r.start <= weekEnd && r.end >= weekStart;
}

export function filterScenariosForBrandSummary(
  scenarios,
  { brandLabel, weekStart, weekEnd } = {}
) {
  const list = Array.isArray(scenarios) ? scenarios : [];
  return list.filter((s) => {
    if (brandLabel != null && String(brandLabel).trim() !== "") {
      if (String(s.brand ?? "").trim() !== String(brandLabel).trim()) {
        return false;
      }
    }
    return scenarioOverlapsWeekSelection(s, weekStart, weekEnd);
  });
}

/**
 * SKU labels attached to visible scenario rows (for copy / transparency).
 * Falls back to `kpis.skuName` when `skus` is empty.
 */
export function computeVisibleSkuStats(visibleScenarios) {
  const list = Array.isArray(visibleScenarios) ? visibleScenarios : [];
  const labels = [];
  for (const s of list) {
    const rowLabels = [];
    if (Array.isArray(s.skus) && s.skus.length) {
      s.skus.forEach((x) => {
        const t = x != null ? String(x).trim() : "";
        if (t) rowLabels.push(t);
      });
    } else if (typeof s.skus === "string" && s.skus.trim()) {
      s.skus
        .split(",")
        .map((x) => String(x).trim())
        .filter(Boolean)
        .forEach((t) => rowLabels.push(t));
    }
    const kn = s.kpis?.skuName;
    if (!rowLabels.length && typeof kn === "string" && kn.trim()) {
      rowLabels.push(kn.trim());
    }
    rowLabels.forEach((t) => labels.push(t));
  }
  const unique = [...new Set(labels)];
  return {
    skuEntryCount: labels.length,
    uniqueSkuCount: unique.length,
  };
}

/**
 * Volume-weighted ROI when volumes exist; otherwise simple average of finite ROIs.
 * All inputs come from scenario.kpis saved with each scenario.
 */
export function aggregateScenarioMetrics(scenarios) {
  let totalVolume = 0;
  let totalContributionMargin = 0;
  let totalDiscountSpend = 0;
  let roiWeighted = 0;
  let roiVol = 0;
  let roiSum = 0;
  let roiCount = 0;
  let cmCounted = false;

  for (const s of scenarios) {
    const k = s.kpis || {};
    const vol = num(k.expectedVolumeL);
    if (vol != null) totalVolume += vol;

    const cm = contributionMarginFromScenarioKpis(k);
    if (cm != null) {
      totalContributionMargin += cm;
      cmCounted = true;
    }

    const spend = num(k.discountSpend);
    if (spend != null) totalDiscountSpend += spend;

    const r = num(k.roi);
    if (r != null) {
      if (vol != null && vol > 0) {
        roiWeighted += vol * r;
        roiVol += vol;
      } else {
        roiSum += r;
        roiCount += 1;
      }
    }
  }

  let roi = null;
  if (roiVol > 0) roi = roiWeighted / roiVol;
  else if (roiCount > 0) roi = roiSum / roiCount;

  return {
    totalVolume,
    totalContributionMargin: cmCounted ? totalContributionMargin : null,
    totalDiscountSpend,
    roi,
  };
}

/**
 * Aggregates KPIs from all saved scenarios in Redux (week selection does not filter this list).
 */
export function resolveBrandSummaryMetrics({
  scenarios,
  brandLabel,
  weekStart,
  weekEnd,
}) {
  const visible = filterScenariosForBrandSummary(scenarios, {
    brandLabel,
    weekStart,
    weekEnd,
  });
  const metrics = aggregateScenarioMetrics(visible);
  const visibleSkuStats = computeVisibleSkuStats(visible);

  return {
    ...metrics,
    visibleScenarioCount: visible.length,
    visibleSkuStats,
  };
}
